import type { LinkTabType } from '@/components/atoms/LinkTab'
import { useEffect, useState } from 'react'

export function useActiveTab(tabs: LinkTabType[], scrollOffset: number = 0) {
	const [activeHref, setActiveHref] = useState<string>('')

	useEffect(() => {
		// Only process anchor links
		const anchorTabs = tabs.filter((tab) => tab.href.startsWith('#'))
		if (anchorTabs.length === 0) {
			setActiveHref('')
			return
		}

		// Function to determine which section should be active based on scroll position
		const updateActiveTab = () => {
			let bestHref = ''
			let bestScore = -Infinity
			const viewportTop = scrollOffset
			const viewportBottom = window.innerHeight / 2

			anchorTabs.forEach((tab) => {
				const id = tab.href.slice(1)
				const element = document.getElementById(id)
				if (!element) return

				const rect = element.getBoundingClientRect()
				const elementTop = rect.top
				const elementBottom = rect.bottom

				// Check if element is in viewport (with some margin)
				const isInViewport = elementBottom > viewportTop && elementTop < viewportBottom

				if (isInViewport) {
					// Calculate score based on:
					// 1. How close the top of the element is to the scroll offset (prefer elements at or above offset)
					// 2. How much of the element is visible
					const distanceFromOffset = Math.max(0, elementTop - viewportTop)
					const visibleHeight =
						Math.min(elementBottom, viewportBottom) - Math.max(elementTop, viewportTop)
					const totalHeight = rect.height
					const visibilityRatio = totalHeight > 0 ? visibleHeight / totalHeight : 0

					// Prefer sections that are at or just above the scroll offset
					// Score is higher for elements closer to the offset and more visible
					// Elements at or above the offset get a bonus
					const offsetBonus = elementTop <= viewportTop + 10 ? 50 : 0
					const score = visibilityRatio * 100 - distanceFromOffset * 0.5 + offsetBonus

					if (score > bestScore) {
						bestScore = score
						bestHref = tab.href
					}
				} else if (elementTop < viewportTop && elementBottom > viewportTop) {
					// Element is partially above viewport but still intersecting
					// This can happen with the scroll offset
					const score = 30 // Lower score than fully visible elements
					if (score > bestScore) {
						bestScore = score
						bestHref = tab.href
					}
				}
			})

			// If no section is in viewport, find the one that was most recently passed
			// (i.e., the one closest above the viewport)
			if (bestHref === '') {
				let closestHref = ''
				let closestDistance = Infinity

				anchorTabs.forEach((tab) => {
					const id = tab.href.slice(1)
					const element = document.getElementById(id)
					if (element) {
						const rect = element.getBoundingClientRect()
						// Prefer elements that are above the viewport (we've scrolled past them)
						if (rect.bottom < viewportTop) {
							const distance = viewportTop - rect.bottom
							if (distance < closestDistance) {
								closestDistance = distance
								closestHref = tab.href
							}
						}
					}
				})

				if (closestHref) {
					bestHref = closestHref
				}
			}

			if (bestHref) {
				setActiveHref(bestHref)
			}
		}

		// Use Intersection Observer for efficient scroll tracking
		const observer = new IntersectionObserver(updateActiveTab, {
			rootMargin: `-${scrollOffset}px 0px -50% 0px`,
			threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
			root: document.body,
		})

		// Observe all anchor target elements
		anchorTabs.forEach((tab) => {
			const id = tab.href.slice(1)
			const element = document.getElementById(id)
			if (element) {
				observer.observe(element)
			}
		})

		// Also update on scroll for more responsive updates
		const handleScroll = () => {
			updateActiveTab()
		}

		window.addEventListener('scroll', handleScroll)
		// Initial update
		updateActiveTab()

		return () => {
			observer.disconnect()
			window.removeEventListener('scroll', handleScroll)
		}
	}, [tabs, scrollOffset])

	return activeHref
}
